const roots = document.querySelectorAll('[data-see-house-lit]');

for (const root of roots) {
  await mountSeeHouseLit(root);
}

export async function mountSeeHouseLit(root) {
  if (!root || root.dataset.editorInitialized === 'true') return;
  root.dataset.editorInitialized = 'true';
  try {
    const core = await import(root.dataset.coreUrl);
    const presetModule = await import(root.dataset.presetRepositoryUrl);
    const generatorModule = await import(root.dataset.generatorUrl);
    initializeEditor(root, core, presetModule, generatorModule);
  } catch (error) {
    delete root.dataset.editorInitialized;
    throw error;
  }
}

function initializeEditor(root, core, presetModule, generatorModule) {
  const $ = (selector) => root.querySelector(selector);
  const isEmbeddedStockDemo = root.dataset.stockDemo === 'true';
  const uploadView = $('[data-upload-view]');
  const workspace = $('[data-workspace]');
  const photoInput = $('[data-photo-input]');
  const uploadMessage = $('[data-upload-message]');
  const resumeButton = $('[data-resume-project]');
  const stage = $('[data-stage]');
  const stageWrap = $('[data-stage-wrap]');
  const image = $('[data-house-image]');
  const canvas = $('[data-led-canvas]');
  const traceLayer = $('[data-trace-layer]');
  const traceHint = $('[data-trace-hint]');
  const nightSlider = $('[data-night-slider]');
  const brightnessSlider = $('[data-brightness-slider]');
  const appBrightness = $('[data-app-brightness]');
  const spacingSlider = $('[data-spacing-slider]');
  const presetBrowser = $('[data-preset-catalog]');
  const presetSearch = $('[data-preset-search]');
  const presetBack = $('[data-preset-back]');
  const presetBackLabel = $('[data-preset-back-label]');
  const presetBreadcrumbs = $('[data-preset-breadcrumbs]');
  const presetStatus = $('[data-preset-status]');
  const nowPlaying = $('[data-now-playing]');
  const effectBrowser = $('[data-effect-catalog]');
  const effectSearch = $('[data-effect-search]');
  const effectCount = $('[data-effect-count]');
  const effectControls = $('[data-effect-controls]');
  const effectPalette = $('[data-effect-palette]');
  const paletteSearch = $('[data-palette-search]');
  const paletteControl = $('[data-palette-control]');
  const paletteNote = $('[data-palette-note]');
  const colorWheel = $('[data-color-wheel]');
  const colorSlots = $('[data-color-slots]');
  const colorValue = $('[data-color-value]');
  const colorTemperature = $('[data-color-temperature]');
  const colorHex = $('[data-color-hex]');
  const cameraDialog = $('[data-camera-dialog]');
  const cameraVideo = $('[data-camera-video]');
  const confirmDialog = $('[data-confirm-dialog]');
  const helpDialog = $('[data-help-dialog]');
  const fullAppDialog = $('[data-full-app-dialog]');
  const fullAppHeading = $('[data-full-app-heading]');
  const fullAppCopy = $('[data-full-app-copy]');
  const presetRepository = new presetModule.GlobalPresetSnapshotRepository({
    sourceUrl: root.dataset.presetCatalogUrl,
    fallbackPresets: core.DEMO_PRESETS,
    mapPreset: core.globalPresetToPreviewPreset
  });
  const effectRepository = new presetModule.EffectCatalogRepository({ sourceUrl: root.dataset.effectsUrl });
  const paletteRepository = new presetModule.PaletteCatalogRepository({ sourceUrl: root.dataset.palettesUrl });
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const alphabetical = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

  const compareByName = (left, right) => alphabetical.compare(left.name, right.name);
  const compareEffects = (left, right) => {
    const leftSolid = left.name.match(/^Solid ([1-5])$/i);
    const rightSolid = right.name.match(/^Solid ([1-5])$/i);
    if (leftSolid && rightSolid) return Number(leftSolid[1]) - Number(rightSolid[1]);
    if (leftSolid) return -1;
    if (rightSolid) return 1;
    return compareByName(left, right);
  };

  const state = {
    version: 1,
    id: createId('project'),
    imageUrl: '',
    imageWidth: 0,
    imageHeight: 0,
    segments: [],
    selectedSegmentId: null,
    selectedPointIndex: null,
    activeSegmentId: null,
    nightLevel: 0.72,
    ledSpacing: 18,
    brightness: 0.9,
    selectedPresetId: 'warm-white',
    selectedEffectId: null,
    effectSpeed: core.DEFAULT_EFFECT_SPEED,
    effectIntensity: 128,
    effectCustom: [128, 128, 16],
    effectOptions: [false, false, false],
    selectedPaletteId: 0,
    customColors: [[255, 36, 36], [34, 216, 107], [40, 184, 255], [255, 200, 0], [255, 0, 220]],
    selectedColorSlot: 0,
    colorsCustomized: false,
    speedCustomized: false,
    intensityCustomized: false,
    paletteCustomized: false,
    appTab: 'colors',
    poweredOn: true,
    playing: !reducedMotion,
    zoom: 1,
    showGuides: true
  };

  let presets = [];
  let presetCatalog = null;
  let effects = [];
  let palettes = [];
  const hydratePresetMetadata = () => {
    presets = presets.map((preset) => core.hydratePresetRendererMetadata(preset, effects, palettes));
  };
  let currentPresetFolderId = null;
  let presetSearchQuery = '';
  let effectSearchQuery = '';
  let effectFilter = 'all';
  let paletteSearchQuery = '';
  let presetCatalogLoaded = false;
  let effectCatalogLoaded = false;
  let animationFrame = 0;
  let animationStarted = performance.now();
  let cameraStream = null;
  let dragging = null;
  let colorWheelDragging = false;
  let history = [];
  let historyIndex = -1;
  let saveTimer = 0;
  let fittedStageWidth = 0;

  function trackVisualizer(action, details = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'showhome_visualizer', visualizer_action: action, ...details });
  }

  trackVisualizer('page_viewed');

  function createId(prefix) {
    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }

  function selectedSegment() {
    return state.segments.find((segment) => segment.id === state.selectedSegmentId) || null;
  }

  function activeSegment() {
    return state.segments.find((segment) => segment.id === state.activeSegmentId) || null;
  }

  function createSegment() {
    const segment = { id: createId('run'), points: [] };
    state.segments.push(segment);
    state.activeSegmentId = segment.id;
    state.selectedSegmentId = segment.id;
    state.selectedPointIndex = null;
    return segment;
  }

  function projectSnapshot() {
    return {
      version: 1,
      id: state.id,
      imageUrl: state.imageUrl,
      imageWidth: state.imageWidth,
      imageHeight: state.imageHeight,
      segments: state.segments,
      nightLevel: state.nightLevel,
      ledSpacing: state.ledSpacing,
      brightness: state.brightness,
      selectedPresetId: state.selectedPresetId,
      selectedEffectId: state.selectedEffectId,
      effectSpeed: state.effectSpeed,
      effectIntensity: state.effectIntensity,
      effectCustom: state.effectCustom,
      effectOptions: state.effectOptions,
      selectedPaletteId: state.selectedPaletteId,
      customColors: state.customColors,
      selectedColorSlot: state.selectedColorSlot,
      colorsCustomized: state.colorsCustomized,
      speedCustomized: state.speedCustomized,
      intensityCustomized: state.intensityCustomized,
      paletteCustomized: state.paletteCustomized,
      appTab: state.appTab,
      poweredOn: state.poweredOn,
      updatedAt: new Date().toISOString()
    };
  }

  function scheduleSave() {
    if (isEmbeddedStockDemo) return;
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => {
      try {
        core.projectStorage.save(projectSnapshot());
        $('[data-save-status]').textContent = 'Saved automatically in this browser.';
      } catch (_error) {
        $('[data-save-status]').textContent = 'This photo is too large for automatic browser storage. Your current session is still safe.';
      }
    }, 450);
  }

  function pushHistory() {
    const snapshot = JSON.stringify(state.segments);
    if (history[historyIndex] === snapshot) return;
    history = history.slice(0, historyIndex + 1);
    history.push(snapshot);
    if (history.length > 60) history.shift();
    historyIndex = history.length - 1;
    updateToolbar();
    scheduleSave();
  }

  function restoreHistory(index) {
    if (index < 0 || index >= history.length) return;
    historyIndex = index;
    state.segments = JSON.parse(history[historyIndex]);
    state.selectedSegmentId = null;
    state.selectedPointIndex = null;
    state.activeSegmentId = null;
    renderTrace();
    updateToolbar();
    scheduleSave();
  }

  function updateNightFilter() {
    const level = Math.pow(state.nightLevel, 1.28);
    stage.style.setProperty('--night-brightness', String(1 - level * 0.76));
    stage.style.setProperty('--night-contrast', String(1 + level * 0.32));
    stage.style.setProperty('--night-saturation', String(1 - level * 0.3));
    stage.style.setProperty('--night-tint', String(level * 0.48));
  }

  function setImage(source, width, height, shouldSave = true) {
    state.imageUrl = source;
    state.imageWidth = width;
    state.imageHeight = height;
    image.src = source;
    stage.style.aspectRatio = `${width} / ${height}`;
    uploadView.hidden = true;
    workspace.hidden = false;
    state.segments = [];
    state.activeSegmentId = null;
    state.selectedSegmentId = null;
    state.selectedPointIndex = null;
    history = [];
    historyIndex = -1;
    pushHistory();
    updateNightFilter();
    setGuidesVisible(true);
    requestAnimationFrame(() => {
      fitStageToScreen();
      resizeLayers();
    });
    workspace.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (shouldSave) scheduleSave();
  }

  async function processFile(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      uploadMessage.textContent = 'Please choose a JPEG, PNG, WebP, HEIC, or HEIF image.';
      trackVisualizer('photo_error', { error_type: 'invalid_file_type' });
      return;
    }
    uploadMessage.textContent = 'Preparing your photo…';
    try {
      const bitmap = await createImageBitmap(file);
      const warnings = [];
      if (bitmap.width < 1000 || bitmap.height < 600) warnings.push('This image is fairly small, so fine tracing may look softer.');
      if (bitmap.height > bitmap.width) warnings.push('Portrait photos work, but landscape will give you more editing room.');
      const maxDimension = 2200;
      const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
      const width = Math.round(bitmap.width * scale);
      const height = Math.round(bitmap.height * scale);
      const processor = document.createElement('canvas');
      processor.width = width;
      processor.height = height;
      const context = processor.getContext('2d', { alpha: false });
      context.drawImage(bitmap, 0, 0, width, height);
      bitmap.close();
      const source = processor.toDataURL('image/jpeg', 0.88);
      uploadMessage.textContent = warnings.join(' ');
      setImage(source, width, height);
      trackVisualizer('photo_added', { photo_source: 'upload' });
    } catch (_error) {
      uploadMessage.textContent = 'That image could not be opened by this browser. Try exporting it as a JPEG first.';
      trackVisualizer('photo_error', { error_type: 'unsupported_image' });
    }
  }

  function stagePoint(event) {
    const rect = traceLayer.getBoundingClientRect();
    return core.pixelsToNormalized({ x: event.clientX - rect.left, y: event.clientY - rect.top }, rect.width, rect.height);
  }

  function addPoint(event) {
    if (event.target.closest('[data-control-point]')) return;
    let segment = activeSegment();
    if (!segment) segment = createSegment();
    segment.points.push(stagePoint(event));
    state.selectedSegmentId = segment.id;
    state.selectedPointIndex = segment.points.length - 1;
    pushHistory();
    renderTrace();
  }

  function renderTrace() {
    const rect = stage.getBoundingClientRect();
    const width = Math.max(1, rect.width);
    const height = Math.max(1, rect.height);
    traceLayer.setAttribute('viewBox', `0 0 ${width} ${height}`);
    traceLayer.replaceChildren();

    state.segments.forEach((segment) => {
      if (!segment.points.length) return;
      const points = segment.points.map((point) => `${point.x * width},${point.y * height}`).join(' ');
      if (segment.points.length > 1) {
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
        line.setAttribute('points', points);
        line.setAttribute('class', `shl-trace-line${segment.id === state.selectedSegmentId ? ' is-selected' : ''}`);
        line.dataset.segmentId = segment.id;
        line.addEventListener('pointerdown', (event) => {
          event.stopPropagation();
          state.selectedSegmentId = segment.id;
          state.selectedPointIndex = null;
          renderTrace();
        });
        traceLayer.append(line);
      }
      segment.points.forEach((point, pointIndex) => {
        const handle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        handle.setAttribute('cx', String(point.x * width));
        handle.setAttribute('cy', String(point.y * height));
        handle.setAttribute('r', window.innerWidth < 750 ? '8' : '7');
        handle.setAttribute('class', `shl-control-point${segment.id === state.selectedSegmentId && pointIndex === state.selectedPointIndex ? ' is-selected' : ''}`);
        handle.dataset.controlPoint = 'true';
        handle.dataset.segmentId = segment.id;
        handle.dataset.pointIndex = String(pointIndex);
        handle.addEventListener('pointerdown', beginDrag);
        traceLayer.append(handle);
      });
    });

    traceHint.hidden = state.segments.some((segment) => segment.points.length);
    updateToolbar();
    updateCounts();
  }

  function setGuidesVisible(visible) {
    state.showGuides = visible;
    root.querySelector('.shl-editor-shell').classList.toggle('is-previewing', !visible);
    traceLayer.setAttribute('aria-hidden', String(!visible));
    $('[data-tool="draw"]').classList.toggle('is-active', visible);
    const toggle = $('[data-toggle-guides]');
    toggle.querySelector('b').textContent = visible ? 'Preview only' : 'Edit guides';
    toggle.title = visible ? 'Hide tracing guides and preview only the lights' : 'Show and edit tracing guides';
    traceHint.hidden = !visible || state.segments.some((segment) => segment.points.length);
  }

  function beginDrag(event) {
    event.stopPropagation();
    event.preventDefault();
    dragging = { segmentId: event.currentTarget.dataset.segmentId, pointIndex: Number(event.currentTarget.dataset.pointIndex) };
    state.selectedSegmentId = dragging.segmentId;
    state.selectedPointIndex = dragging.pointIndex;
    traceLayer.setPointerCapture(event.pointerId);
    renderTrace();
  }

  function moveDrag(event) {
    if (!dragging) return;
    const segment = state.segments.find((entry) => entry.id === dragging.segmentId);
    if (!segment || !segment.points[dragging.pointIndex]) return;
    segment.points[dragging.pointIndex] = stagePoint(event);
    renderTrace();
  }

  function endDrag() {
    if (!dragging) return;
    dragging = null;
    pushHistory();
  }

  function ledPositions() {
    const rect = stage.getBoundingClientRect();
    const spacingPixels = state.ledSpacing * Math.max(0.62, rect.width / 1200);
    return state.segments.flatMap((segment) => core.generateLedPositions(segment.points, spacingPixels, rect.width, rect.height));
  }

  function updateCounts() {
    $('[data-run-count]').textContent = String(state.segments.filter((segment) => segment.points.length > 1).length);
    $('[data-led-count]').textContent = String(ledPositions().length);
  }

  function resizeLayers() {
    const rect = stage.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const ratio = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    canvas.dataset.ratio = String(ratio);
    renderTrace();
  }

  function fitStageToScreen() {
    if (!state.imageWidth || !state.imageHeight || !stageWrap.clientWidth) return;
    const aspect = state.imageWidth / state.imageHeight;
    const mobile = window.innerWidth < 750;
    const availableHeight = mobile
      ? Math.min(430, Math.max(220, window.innerHeight * 0.44))
      : Math.min(720, Math.max(320, window.innerHeight - 300));
    fittedStageWidth = Math.min(stageWrap.clientWidth, availableHeight * aspect);
    stage.style.width = `${Math.round(fittedStageWidth * state.zoom)}px`;
  }

  function normalizedControlColors(colors, slotCount = 3) {
    // Preserve all five firmware positions. Effect metadata controls which
    // buttons are visible; truncating this array renumbers sparse slots.
    const targetCount = 5;
    const output = (Array.isArray(colors) ? colors : [])
      .filter((color) => Array.isArray(color) && color.length >= 3)
      .map((color) => color.slice(0, 3).map((channel) => core.clamp(Math.round(Number(channel) || 0), 0, 255)))
      .slice(0, targetCount);
    const defaults = [[255, 36, 36], [34, 216, 107], [40, 184, 255], [255, 200, 0], [255, 0, 220]];
    while (output.length < targetCount) output.push([...defaults[output.length]]);
    return output;
  }

  function selectedPreset() {
    return presets.find((preset) => preset.id === state.selectedPresetId) || presets[0] || null;
  }

  function selectedEffect() {
    return effects.find((effect) => effect.id === state.selectedEffectId) || null;
  }

  function paletteTargetEffect() {
    const effect = selectedEffect();
    if (effect) return effect;
    const presetEffectId = Number(selectedPreset()?.segments?.[0]?.effect);
    return effects.find((entry) => entry.id === presetEffectId) || null;
  }

  function paletteStopsFor(effect, paletteId) {
    if (!effect?.supportsPalette) return [];
    const controlColors = normalizedControlColors(state.customColors, 3);
    // Firmware palette 0 normally means the effect's selected color slots,
    // not Party. Only effects that directly load a default palette resolve to
    // one of these built-ins (FX_fcn.cpp Segment::loadPalette).
    const firmwareDefaults = new Map([[65, 6], [66, 35], [74, 6], [78, 3], [87, 11], [104, 35], [110, 6], [125, 11]]);
    const resolvedId = paletteId === 0 ? firmwareDefaults.get(effect.id) : paletteId;
    if (resolvedId === undefined) return [];
    if ([2, 3, 4, 5].includes(resolvedId)) return core.buildFirmwareColorPalette(resolvedId, controlColors);
    return palettes.find((palette) => palette.id === resolvedId)?.stops || [];
  }

  function activeColorSlotCount() {
    const effect = selectedEffect();
    if (effect) return effect.colorSlots;
    if (state.selectedEffectId !== null) return core.clamp(state.customColors.length || 3, 1, 5);
    const preset = selectedPreset();
    const presetEffectId = Number(preset?.segments?.[0]?.effect);
    const presetEffect = effects.find((catalogEffect) => catalogEffect.id === presetEffectId);
    if (presetEffect) return presetEffect.colorSlots;
    const presetColorCount = preset?.colors?.length || state.customColors.length || 3;
    return core.clamp(presetColorCount, 1, 5);
  }

  function activeColorSlotIndices() {
    const effect = selectedEffect();
    const preset = selectedPreset();
    const presetSegment = preset?.segments?.[0] || {};
    const metadata = effect || effects.find((entry) => entry.id === Number(presetSegment.effect));
    const indices = new Set(Array.isArray(metadata?.colorSlotIndices) ? metadata.colorSlotIndices : []);
    const paletteId = effect || state.paletteCustomized
      ? state.selectedPaletteId
      : Number(presetSegment.palette) || 0;
    if (paletteId === 2) indices.add(0);
    if (paletteId === 3) { indices.add(0); indices.add(1); }
    if (paletteId === 4 || paletteId === 5) { indices.add(0); indices.add(1); indices.add(2); }
    if (!metadata && preset?.colors?.length) {
      for (let index = 0; index < Math.min(5, preset.colors.length); index += 1) indices.add(index);
    }
    return [...indices].filter((index) => index >= 0 && index < 5).sort((left, right) => left - right);
  }

  function activePreviewPreset() {
    const catalogEffect = selectedEffect();
    if (catalogEffect || state.selectedEffectId !== null) {
      // A saved project can be resumed before the effect catalogue request has
      // completed. Keep rendering its selected effect during that brief window
      // instead of silently falling back to the previously selected preset.
      const previewEffect = catalogEffect || {
        id: state.selectedEffectId,
        name: `Effect ${state.selectedEffectId}`,
        previewFamily: ''
      };
      return core.effectToPreviewPreset(previewEffect, {
        colors: state.customColors,
        speed: state.effectSpeed,
        intensity: state.effectIntensity,
        custom1: state.effectCustom[0],
        custom2: state.effectCustom[1],
        custom3: state.effectCustom[2],
        option1: state.effectOptions[0],
        option2: state.effectOptions[1],
        option3: state.effectOptions[2],
        palette: previewEffect.supportsPalette ? state.selectedPaletteId : 0,
        paletteStops: paletteStopsFor(previewEffect, state.selectedPaletteId)
      });
    }
    const preset = selectedPreset();
    if (!preset) return null;
    const controlledPreset = core.applyPreviewControls(preset, {
      ...(state.colorsCustomized ? { colors: state.customColors } : {}),
      ...(state.speedCustomized ? { speed: state.effectSpeed } : {}),
      ...(state.intensityCustomized ? { intensity: state.effectIntensity } : {})
    });
    if (!state.paletteCustomized) return controlledPreset;
    return {
      ...controlledPreset,
      segments: (controlledPreset.segments || []).map((segment) => {
        const effect = effects.find((entry) => entry.id === Number(segment.effect));
        if (!effect?.supportsPalette) return segment;
        return {
          ...segment,
          palette: state.selectedPaletteId,
          paletteStops: paletteStopsFor(effect, state.selectedPaletteId)
        };
      })
    };
  }

  function syncControlOutputs() {
    $('[data-app-brightness-output]').textContent = `${Math.round(state.brightness * 100)}%`;
    renderEffectControls();
  }

  function renderEffectControls() {
    effectControls.replaceChildren();
    const effect = selectedEffect();
    if (!effect) return;
    (effect.controls || []).forEach((control) => {
      const label = document.createElement('label');
      if (control.type === 'option') {
        const index = Number(control.key.slice(-1)) - 1;
        label.className = 'shl-effect-option';
        label.innerHTML = `<span>${control.label}</span><input type="checkbox" ${state.effectOptions[index] ? 'checked' : ''}>`;
        label.querySelector('input').addEventListener('change', (event) => {
          state.effectOptions[index] = event.target.checked;
          animationStarted = performance.now();
          scheduleSave();
        });
      } else {
        const value = control.key === 'speed' ? state.effectSpeed : control.key === 'intensity' ? state.effectIntensity : state.effectCustom[Number(control.key.slice(-1)) - 1];
        label.innerHTML = `<span>${control.label}</span><input type="range" min="0" max="${control.max ?? 255}" value="${value}" aria-label="${control.label}"><output>${value}</output>`;
        label.querySelector('input').addEventListener('input', (event) => {
          const next = Number(event.target.value);
          if (control.key === 'speed') { state.effectSpeed = next; state.speedCustomized = true; }
          else if (control.key === 'intensity') { state.effectIntensity = next; state.intensityCustomized = true; }
          else state.effectCustom[Number(control.key.slice(-1)) - 1] = next;
          label.querySelector('output').textContent = String(next);
          animationStarted = performance.now();
          scheduleSave();
        });
      }
      effectControls.append(label);
    });
  }

  function syncPowerControl() {
    const toggle = $('[data-play-toggle]');
    toggle.setAttribute('aria-pressed', String(state.poweredOn));
    toggle.classList.toggle('is-on', state.poweredOn);
    toggle.setAttribute('aria-label', state.poweredOn ? 'Turn preview lights off' : 'Turn preview lights on');
    $('[data-play-label]').textContent = state.poweredOn ? 'Lights on' : 'Lights off';
  }

  function ensurePreviewPlaying() {
    state.poweredOn = true;
    state.playing = true;
    syncPowerControl();
  }

  function palettePreviewStops(palette) {
    if ([2, 3, 4, 5].includes(palette.id)) return paletteStopsFor(paletteTargetEffect(), palette.id);
    if (palette.id === 0) {
      const firmwareStops = paletteStopsFor(paletteTargetEffect(), 0);
      if (firmwareStops.length) return firmwareStops;
      const colors = normalizedControlColors(state.customColors, 3);
      return colors.map((color, index) => ({ position: Math.round(index * 255 / Math.max(1, colors.length - 1)), color }));
    }
    return palette.stops;
  }

  function paletteGradient(palette) {
    const stops = palettePreviewStops(palette);
    if (!stops.length) return 'linear-gradient(90deg,#666,#999)';
    return `linear-gradient(90deg,${stops.map((stop) => `rgb(${stop.color.join(',')}) ${Math.round(stop.position / 255 * 100)}%`).join(',')})`;
  }

  function renderPalettes() {
    effectPalette.replaceChildren();
    const query = paletteSearchQuery.trim().toLowerCase();
    const visible = palettes.filter((palette) => !query || palette.name.toLowerCase().includes(query));
    visible.forEach((palette) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `shl-palette-button${palette.id === state.selectedPaletteId ? ' is-active' : ''}`;
      button.disabled = !paletteTargetEffect()?.supportsPalette;
      button.setAttribute('aria-pressed', String(palette.id === state.selectedPaletteId));
      button.innerHTML = `<span>${palette.name}</span><i style="--palette-gradient:${paletteGradient(palette)}" aria-hidden="true"></i>`;
      button.addEventListener('click', () => {
        state.selectedPaletteId = palette.id;
        state.paletteCustomized = true;
        animationStarted = performance.now();
        renderColorControls();
        renderPalettes();
        renderPaletteControl();
        scheduleSave();
      });
      effectPalette.append(button);
    });
    if (!visible.length) {
      const empty = document.createElement('p');
      empty.className = 'shl-phone__empty';
      empty.textContent = `No palettes found for “${paletteSearchQuery.trim()}”.`;
      effectPalette.append(empty);
    }
  }

  function renderPaletteControl() {
    const effect = paletteTargetEffect();
    const supported = Boolean(effect?.supportsPalette);
    paletteControl.hidden = false;
    paletteControl.classList.toggle('is-disabled', !supported);
    effectPalette.querySelectorAll('button').forEach((button) => { button.disabled = !supported; });
    paletteNote.textContent = supported
      ? `🎨 ${palettes.find((palette) => palette.id === state.selectedPaletteId)?.name || 'Default'} palette selected`
      : effect
        ? '🎨 Color palette not used by this effect'
        : '🎨 Choose an effect first to use a palette';
  }

  function updateNowPlaying() {
    nowPlaying.textContent = selectedEffect()?.name || selectedPreset()?.name || 'Choose a look';
  }

  function selectPreset(preset) {
    if (!preset) return;
    const firstSegment = preset.segments?.[0] || {};
    state.selectedPresetId = preset.id;
    state.selectedEffectId = null;
    state.effectSpeed = Number.isFinite(Number(firstSegment.speed)) ? Number(firstSegment.speed) : 128;
    state.effectIntensity = Number.isFinite(Number(firstSegment.intensity)) ? Number(firstSegment.intensity) : 128;
    state.effectCustom = [firstSegment.custom1 ?? 128, firstSegment.custom2 ?? 128, firstSegment.custom3 ?? 16];
    state.effectOptions = [Boolean(firstSegment.option1), Boolean(firstSegment.option2), Boolean(firstSegment.option3)];
    state.selectedPaletteId = Number.isFinite(Number(firstSegment.palette)) ? Number(firstSegment.palette) : 0;
    state.customColors = normalizedControlColors(firstSegment.colors || preset.colors, 5);
    state.colorsCustomized = false;
    state.speedCustomized = false;
    state.intensityCustomized = false;
    state.paletteCustomized = false;
    ensurePreviewPlaying();
    animationStarted = performance.now();
    syncControlOutputs();
    updateNowPlaying();
    renderPresets();
    renderEffects();
    renderColorControls();
    renderPalettes();
    renderPaletteControl();
    scheduleSave();
    updateSteps(4);
  }

  function selectEffect(effect, colors = null, overrides = {}) {
    if (!effect) return;
    state.selectedEffectId = effect.id;
    const defaults = effect.defaults || {};
    const speedControl = (effect.controls || []).find((control) => control.key === 'speed');
    const explicitSpeed = defaults.sx ?? core.DEFAULT_EFFECT_SPEED;
    // A normal Speed slider starts deliberately slow in the house preview.
    // Preserve firmware defaults for semantic controls such as Fire 2012's
    // Cooling and Sunrise's Time because lowering those changes the effect,
    // not merely its playback rate.
    const defaultSpeed = speedControl && !/^speed$/i.test(speedControl.label)
      ? explicitSpeed
      : Math.min(explicitSpeed, core.DEFAULT_EFFECT_SPEED);
    state.effectSpeed = Number.isFinite(Number(overrides.speed))
      ? core.clamp(Math.round(Number(overrides.speed)), 0, 255)
      : defaultSpeed;
    state.effectIntensity = Number.isFinite(Number(overrides.intensity))
      ? core.clamp(Math.round(Number(overrides.intensity)), 0, 255)
      : (defaults.ix ?? 128);
    state.effectCustom = [defaults.c1 ?? 128, defaults.c2 ?? 128, defaults.c3 ?? 16];
    state.effectOptions = [Boolean(defaults.o1), Boolean(defaults.o2), Boolean(defaults.o3)];
    state.selectedPaletteId = effect.supportsPalette
      ? (Number.isFinite(Number(overrides.palette)) ? core.clamp(Math.round(Number(overrides.palette)), 0, 255) : (defaults.pal ?? 0))
      : 0;
    state.customColors = normalizedControlColors(colors || state.customColors, effect.colorSlots);
    if (!colors && !state.customColors.some((color) => color.some((channel) => channel > 0))) {
      state.customColors = normalizedControlColors([[255, 36, 36], [34, 216, 107], [40, 184, 255]], effect.colorSlots);
    }
    state.selectedColorSlot = core.clamp(state.selectedColorSlot, 0, state.customColors.length - 1);
    state.colorsCustomized = true;
    state.speedCustomized = true;
    state.intensityCustomized = true;
    state.paletteCustomized = true;
    ensurePreviewPlaying();
    animationStarted = performance.now();
    syncControlOutputs();
    updateNowPlaying();
    renderPresets();
    renderEffects();
    renderColorControls();
    renderPalettes();
    renderPaletteControl();
    scheduleSave();
    updateSteps(4);
  }

  function rgbToHsv([red, green, blue]) {
    const r = red / 255;
    const g = green / 255;
    const b = blue / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    let hue = 0;
    if (delta) {
      if (max === r) hue = 60 * (((g - b) / delta) % 6);
      else if (max === g) hue = 60 * ((b - r) / delta + 2);
      else hue = 60 * ((r - g) / delta + 4);
    }
    return { h: (hue + 360) % 360, s: max ? delta / max : 0, v: max };
  }

  function hsvToRgb(hue, saturation, value) {
    const chroma = value * saturation;
    const sector = ((hue % 360) + 360) % 360 / 60;
    const x = chroma * (1 - Math.abs(sector % 2 - 1));
    const choices = [[chroma, x, 0], [x, chroma, 0], [0, chroma, x], [0, x, chroma], [x, 0, chroma], [chroma, 0, x]];
    const [r, g, b] = choices[Math.floor(sector) % 6];
    const match = value - chroma;
    return [r, g, b].map((channel) => Math.round((channel + match) * 255));
  }

  function rgbToHex(color) {
    return `#${color.map((channel) => Math.round(channel).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
  }

  function hexToRgb(value) {
    const match = String(value || '').trim().match(/^#?([0-9a-f]{6})$/i);
    if (!match) return null;
    return [0, 2, 4].map((offset) => parseInt(match[1].slice(offset, offset + 2), 16));
  }

  // Matches the same 1,900K–10,091K temperature conversion used by the
  // Flutter app's ColorTemperatureSlider.
  function colorFromTemperatureValue(value) {
    const kelvin = 1900 + core.clamp(Number(value), 0, 255) * (10091 - 1900) / 255;
    const temperature = kelvin / 100;
    let red;
    let green;
    let blue;
    if (temperature < 66) {
      red = 255;
      green = -155.25485562709179 - 0.44596950469579133 * (temperature - 2) + 104.49216199393888 * Math.log(temperature - 2);
      blue = temperature < 20 ? 0 : -254.76935184120902 + 0.8274096064007395 * (temperature - 10) + 115.67994401066147 * Math.log(temperature - 10);
    } else {
      red = 351.97690566805693 + 0.114206453784165 * (temperature - 55) - 40.25366309332127 * Math.log(temperature - 55);
      green = 325.4494125711974 + 0.07943456536662342 * (temperature - 50) - 28.0852963507957 * Math.log(temperature - 50);
      blue = 255;
    }
    return [red, green, blue].map((channel) => core.clamp(Math.floor(channel), 0, 255));
  }

  function setSelectedColor(color) {
    state.customColors[state.selectedColorSlot] = color.map((channel) => core.clamp(Math.round(channel), 0, 255));
    state.colorsCustomized = true;
    animationStarted = performance.now();
    renderColorControls();
    renderPalettes();
    scheduleSave();
  }

  function renderColorControls() {
    state.customColors = normalizedControlColors(state.customColors, activeColorSlotCount());
    const visibleIndices = activeColorSlotIndices();
    if (visibleIndices.length && !visibleIndices.includes(state.selectedColorSlot)) state.selectedColorSlot = visibleIndices[0];
    colorSlots.replaceChildren();
    visibleIndices.forEach((index) => {
      const color = state.customColors[index];
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `shl-color-slot${index === state.selectedColorSlot ? ' is-active' : ''}`;
      button.style.setProperty('--slot-color', `rgb(${color.join(',')})`);
      button.textContent = String(index + 1);
      button.style.color = color[0] * 0.299 + color[1] * 0.587 + color[2] * 0.114 > 128 ? '#111' : '#fff';
      button.setAttribute('aria-label', `Edit color ${index + 1}`);
      button.setAttribute('aria-pressed', String(index === state.selectedColorSlot));
      button.addEventListener('click', () => {
        state.selectedColorSlot = index;
        renderColorControls();
      });
      colorSlots.append(button);
    });
    const hasEditableColors = visibleIndices.length > 0;
    colorWheel.style.pointerEvents = hasEditableColors ? '' : 'none';
    colorWheel.setAttribute('aria-disabled', String(!hasEditableColors));
    colorValue.disabled = !hasEditableColors;
    colorTemperature.disabled = !hasEditableColors;
    colorHex.disabled = !hasEditableColors;
    if (!hasEditableColors) {
      const note = document.createElement('span');
      note.className = 'shl-color-slot-note';
      note.textContent = 'This effect controls color through its palette.';
      colorSlots.append(note);
      return;
    }
    const hsv = rgbToHsv(state.customColors[state.selectedColorSlot]);
    const radius = hsv.s * 50;
    const radians = hsv.h * Math.PI / 180;
    colorWheel.style.setProperty('--wheel-x', `${50 + Math.cos(radians) * radius}%`);
    colorWheel.style.setProperty('--wheel-y', `${50 + Math.sin(radians) * radius}%`);
    colorWheel.setAttribute('aria-valuenow', String(Math.round(hsv.h)));
    colorValue.value = String(Math.round(hsv.v * 255));
    $('[data-color-value-output]').textContent = String(Math.round(hsv.v * 255));
    colorHex.value = rgbToHex(state.customColors[state.selectedColorSlot]);
  }

  function updateColorFromWheel(event) {
    const rect = colorWheel.getBoundingClientRect();
    const dx = event.clientX - rect.left - rect.width / 2;
    const dy = event.clientY - rect.top - rect.height / 2;
    const saturation = core.clamp(Math.hypot(dx, dy) / (rect.width / 2));
    const hue = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
    const value = rgbToHsv(state.customColors[state.selectedColorSlot]).v;
    setSelectedColor(hsvToRgb(hue, saturation, value));
  }

  function switchAppTab(tab, shouldSave = true) {
    if (!['colors', 'effects', 'generator', 'presets'].includes(tab)) return;
    if (tab !== 'generator') designer.closeSuggestions();
    state.appTab = tab;
    root.querySelectorAll('[data-app-panel]').forEach((panel) => {
      const active = panel.dataset.appPanel === tab;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
    root.querySelectorAll('[data-app-tab]').forEach((button) => {
      const active = button.dataset.appTab === tab;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-current', active ? 'page' : 'false');
    });
    if (tab === 'colors') renderColorControls();
    if (tab === 'effects') renderEffects();
    if (shouldSave) scheduleSave();
  }

  function drawFrame(now) {
    // Queue the next frame first so one malformed preset cannot permanently
    // stop the preview loop and force a browser-storage reset.
    animationFrame = requestAnimationFrame(drawFrame);
    const context = canvas.getContext('2d');
    const ratio = Number(canvas.dataset.ratio || 1);
    const width = canvas.width / ratio;
    const height = canvas.height / ratio;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, width, height);
    const positions = ledPositions();
    const preset = activePreviewPreset();

    if (preset && positions.length && state.poweredOn) {
      const elapsedTime = state.playing ? (now - animationStarted) / 1000 : 0;
      const colors = core.renderFrame({ ledCount: positions.length, preset, elapsedTime, brightness: state.brightness });
      const sourceScale = core.clamp(width / 1200, 0.65, 1.25);
      context.globalCompositeOperation = 'screen';

      // Real soffit lights create most of their impact on the wall, not in the
      // diode itself. Two clipped, overlapping washes give us the bright band
      // below the roofline and the softer falloff farther down the facade.
      const drawWash = (x, y, halfWidth, depth, centerOffset, rgb, peakAlpha) => {
        context.save();
        context.beginPath();
        context.rect(
          x - halfWidth * 1.15,
          y - sourceScale,
          halfWidth * 2.3,
          depth + centerOffset + sourceScale * 3
        );
        context.clip();
        context.translate(x, y + centerOffset);
        context.scale(1, depth / halfWidth);
        const wash = context.createRadialGradient(0, 0, 0, 0, 0, halfWidth);
        wash.addColorStop(0, `rgba(${rgb},${peakAlpha})`);
        wash.addColorStop(0.14, `rgba(${rgb},${peakAlpha * 0.9})`);
        wash.addColorStop(0.48, `rgba(${rgb},${peakAlpha * 0.42})`);
        wash.addColorStop(1, `rgba(${rgb},0)`);
        context.fillStyle = wash;
        context.beginPath();
        context.arc(0, 0, halfWidth, 0, Math.PI * 2);
        context.fill();
        context.restore();
      };

      const drawGroundBounce = (x, y, halfWidth, depth, rgb, peakAlpha) => {
        context.save();
        context.translate(x, y);
        context.scale(1, depth / halfWidth);
        const bounce = context.createRadialGradient(0, 0, 0, 0, 0, halfWidth);
        bounce.addColorStop(0, `rgba(${rgb},${peakAlpha})`);
        bounce.addColorStop(0.3, `rgba(${rgb},${peakAlpha * 0.66})`);
        bounce.addColorStop(0.72, `rgba(${rgb},${peakAlpha * 0.2})`);
        bounce.addColorStop(1, `rgba(${rgb},0)`);
        context.fillStyle = bounce;
        context.beginPath();
        context.arc(0, 0, halfWidth, 0, Math.PI * 2);
        context.fill();
        context.restore();
      };

      // A low-energy, long throw overlaps across the entire run. This is what
      // makes the house read as illuminated instead of showing isolated cones.
      positions.forEach((point, index) => {
        if (index % 2) return;
        const color = colors[index];
        const x = point.x * width;
        const y = point.y * height;
        const alpha = Math.pow(color.brightness ?? 1, 0.72);
        const rgb = `${color.r},${color.g},${color.b}`;
        const availableDepth = Math.max(70 * sourceScale, height - y);
        const depth = Math.min(270 * sourceScale, availableDepth * 0.76);

        drawWash(x, y, 68 * sourceScale, depth, 21 * sourceScale, rgb, 0.065 * alpha);
      });

      positions.forEach((point, index) => {
        const color = colors[index];
        const x = point.x * width;
        const y = point.y * height;
        const alpha = Math.pow(color.brightness ?? 1, 0.7);
        const rgb = `${color.r},${color.g},${color.b}`;

        drawWash(x, y, 36 * sourceScale, 130 * sourceScale, 20 * sourceScale, rgb, 0.13 * alpha);
        drawWash(x, y, 18 * sourceScale, 48 * sourceScale, 7 * sourceScale, rgb, 0.26 * alpha);
      });

      // Lower rooflines also throw a faint, wide reflection onto driveways,
      // lawns, and front steps. Sampling every third lamp keeps this soft and
      // continuous without making the browser redraw hundreds of large pools.
      const groundY = height * 0.88;
      positions.forEach((point, index) => {
        if (index % 3 || point.y * height < height * 0.36) return;
        const color = colors[index];
        const alpha = Math.pow(color.brightness ?? 1, 0.72);
        drawGroundBounce(
          point.x * width,
          groundY,
          100 * sourceScale,
          38 * sourceScale,
          `${color.r},${color.g},${color.b}`,
          0.12 * alpha
        );
      });

      // Keep the visible lamp small and crisp: a near-white pin core, a tight
      // coloured halo, and no oversized circular blob.
      positions.forEach((point, index) => {
        const color = colors[index];
        const x = point.x * width;
        const y = point.y * height;
        const alpha = Math.pow(color.brightness ?? 1, 0.62);
        const rgb = `${color.r},${color.g},${color.b}`;
        const glowRadius = 7.6 * sourceScale;

        const glow = context.createRadialGradient(x, y, 0, x, y, glowRadius);
        glow.addColorStop(0, `rgba(255,255,250,${alpha})`);
        glow.addColorStop(0.12, `rgba(${rgb},${0.96 * alpha})`);
        glow.addColorStop(0.38, `rgba(${rgb},${0.34 * alpha})`);
        glow.addColorStop(1, `rgba(${rgb},0)`);
        context.fillStyle = glow;
        context.beginPath();
        context.arc(x, y, glowRadius, 0, Math.PI * 2);
        context.fill();
        context.fillStyle = `rgba(255,255,248,${Math.min(1, 0.96 * alpha)})`;
        context.beginPath();
        context.arc(x, y, Math.max(1.15, 1.35 * sourceScale), 0, Math.PI * 2);
        context.fill();
      });
      context.globalCompositeOperation = 'source-over';
    }
  }

  function updateToolbar() {
    const segment = selectedSegment();
    $('[data-undo]').disabled = historyIndex <= 0;
    $('[data-redo]').disabled = historyIndex >= history.length - 1;
    $('[data-delete-point]').disabled = !(segment && state.selectedPointIndex !== null);
    $('[data-delete-segment]').disabled = !segment;
    $('[data-clear-segments]').disabled = !state.segments.length;
    $('[data-finish-segment]').disabled = !activeSegment();
  }

  function updateSteps(activeStep) {
    root.querySelectorAll('[data-step-button]').forEach((button) => {
      const step = Number(button.dataset.stepButton);
      button.classList.toggle('is-active', step === activeStep);
      button.classList.toggle('is-complete', step < activeStep);
    });
  }

  function renderPresets() {
    if (!presetCatalog) return;
    presetBrowser.replaceChildren();
    const folderById = new Map(presetCatalog.folders.map((folder) => [folder.id, folder]));
    const presetById = new Map(presets.map((preset) => [preset.id, preset]));
    const currentFolder = folderById.get(currentPresetFolderId) || folderById.get(presetCatalog.rootFolderId);
    currentPresetFolderId = currentFolder?.id || presetCatalog.rootFolderId;
    const path = [];
    let pathFolder = currentFolder;
    while (pathFolder && path.length < 10) {
      path.unshift(pathFolder);
      pathFolder = pathFolder.parentId ? folderById.get(pathFolder.parentId) : null;
    }
    presetBreadcrumbs.textContent = path.map((folder) => folder.name).join(' / ') || 'Global Presets';
    presetBack.hidden = !currentFolder?.parentId || Boolean(presetSearchQuery);
    const parentFolder = currentFolder?.parentId ? folderById.get(currentFolder.parentId) : null;
    const backDestination = parentFolder?.name || 'Global Presets';
    presetBackLabel.textContent = backDestination;
    presetBack.setAttribute('aria-label', `Back to ${backDestination}`);

    const query = presetSearchQuery.trim().toLowerCase();
    const visiblePresets = query
      ? presets.filter((preset) => preset.name.toLowerCase().includes(query))
      : (currentFolder?.presets || []).map((id) => presetById.get(id)).filter(Boolean);
    const visibleFolders = query
      ? []
      : (currentFolder?.subfolders || []).map((id) => folderById.get(id)).filter(Boolean);
    visiblePresets.sort(compareByName);
    visibleFolders.sort(compareByName);

    if (visibleFolders.length) {
      const folderGrid = document.createElement('div');
      folderGrid.className = 'shl-phone-folders';
      visibleFolders.forEach((folder) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'shl-phone-folder';
        const icon = document.createElement('span');
        icon.textContent = folder.name.match(/sport/i) ? '★' : folder.name.match(/pot/i) ? '◉' : '✦';
        const copy = document.createElement('div');
        const title = document.createElement('b');
        title.textContent = folder.name;
        const count = document.createElement('small');
        count.textContent = `${folder.presets.length + folder.subfolders.length} choices`;
        const arrow = document.createElement('i');
        arrow.textContent = '›';
        copy.append(title, count);
        button.append(icon, copy, arrow);
        button.addEventListener('click', () => {
          currentPresetFolderId = folder.id;
          renderPresets();
          presetBrowser.scrollTop = 0;
        });
        folderGrid.append(button);
      });
      presetBrowser.append(folderGrid);
    }

    const presetList = document.createElement('div');
    presetList.className = 'shl-phone-presets';
    visiblePresets.forEach((preset) => {
      const button = document.createElement('button');
      button.type = 'button';
      const active = state.selectedEffectId === null && preset.id === state.selectedPresetId;
      button.className = `shl-phone-preset${active ? ' is-active' : ''}`;
      button.dataset.presetId = preset.id;
      button.setAttribute('aria-pressed', String(active));
      const swatches = preset.colors.length ? preset.colors : [[255, 48, 91], [255, 200, 30], [28, 190, 255]];
      const swatchGroup = document.createElement('span');
      swatchGroup.className = 'shl-preset__swatches';
      swatches.slice(0, 5).forEach((color) => {
        const swatch = document.createElement('i');
        swatch.style.backgroundColor = `rgb(${color.join(',')})`;
        swatchGroup.append(swatch);
      });
      const copy = document.createElement('span');
      const title = document.createElement('b');
      title.textContent = preset.name;
      const type = document.createElement('small');
      type.textContent = preset.animated === false || preset.kind === 'static' || preset.kind === 'alternating' ? 'Static look' : 'Animated effect';
      copy.append(title, type);
      const play = document.createElement('em');
      play.textContent = active ? '●' : '▶';
      button.append(swatchGroup, copy, play);
      button.addEventListener('click', () => {
        selectPreset(preset);
      });
      presetList.append(button);
    });
    presetBrowser.append(presetList);

    if (!visibleFolders.length && !visiblePresets.length) {
      const empty = document.createElement('p');
      empty.className = 'shl-phone__empty';
      empty.textContent = query ? `No presets found for “${presetSearchQuery.trim()}”.` : 'This folder is empty.';
      presetBrowser.append(empty);
    }

    updateNowPlaying();
  }

  function renderEffects() {
    if (!effectBrowser) return;
    effectBrowser.replaceChildren();
    const query = effectSearchQuery.trim().toLowerCase();
    const newEffects = new Set(['Solid 2', 'Solid 3', 'Solid 4', 'Solid 5', 'Tide', 'Glints', 'Pulse Train', 'Chase 4', 'Chase 5', 'Stack', 'Chase Tail', 'Stack 3 Colors']);
    const visibleEffects = effects.filter((effect) => effect.visibleInApp !== false
      && (effectFilter === 'all' || (effectFilter === 'new' ? newEffects.has(effect.name)
        : effectFilter === 'static' ? effect.name.toLowerCase().includes('solid') : !effect.name.toLowerCase().includes('solid')))
      && (!query
      || effect.name.toLowerCase().includes(query)
      || effect.category.includes(query))).sort(compareEffects);
    effectCount.textContent = String(visibleEffects.length);
    const list = document.createElement('div');
    list.className = 'shl-effect-list';
    visibleEffects.forEach((effect) => {
      const button = document.createElement('button');
      button.type = 'button';
      const active = effect.id === state.selectedEffectId;
      button.className = `shl-effect-button${active ? ' is-active' : ''}`;
      button.dataset.effectId = String(effect.id);
      button.setAttribute('aria-pressed', String(active));
      const indicator = document.createElement('i');
      const label = document.createElement('span');
      label.textContent = effect.name;
      button.append(indicator, label);
      button.addEventListener('click', () => selectEffect(effect));
      list.append(button);
    });
    effectBrowser.append(list);
    if (!visibleEffects.length) {
      const empty = document.createElement('p');
      empty.className = 'shl-phone__empty';
      empty.textContent = `No effects found for “${effectSearchQuery.trim()}”.`;
      effectBrowser.append(empty);
    }
  }

  async function openCamera() {
    try {
      cameraStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false });
      cameraVideo.srcObject = cameraStream;
      cameraDialog.showModal();
      updateCameraOrientation();
    } catch (_error) {
      uploadMessage.textContent = 'Camera access was unavailable. You can still upload an existing photo.';
      trackVisualizer('photo_error', { error_type: 'camera_unavailable' });
    }
  }

  function closeCamera() {
    if (cameraStream) cameraStream.getTracks().forEach((track) => track.stop());
    cameraStream = null;
    cameraVideo.srcObject = null;
    if (cameraDialog.open) cameraDialog.close();
  }

  function updateCameraOrientation() {
    const portrait = window.innerHeight > window.innerWidth;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    cameraDialog.style.setProperty('--camera-viewport-height', `${Math.round(viewportHeight)}px`);
    cameraDialog.classList.toggle('is-landscape', !portrait);
    $('[data-camera-orientation]').hidden = !portrait;
    $('[data-orientation-hint]').hidden = !portrait;
  }

  function capturePhoto() {
    if (!cameraVideo.videoWidth) return;
    const processor = document.createElement('canvas');
    const maxDimension = 2200;
    const scale = Math.min(1, maxDimension / Math.max(cameraVideo.videoWidth, cameraVideo.videoHeight));
    processor.width = Math.round(cameraVideo.videoWidth * scale);
    processor.height = Math.round(cameraVideo.videoHeight * scale);
    processor.getContext('2d', { alpha: false }).drawImage(cameraVideo, 0, 0, processor.width, processor.height);
    const source = processor.toDataURL('image/jpeg', 0.88);
    const width = processor.width;
    const height = processor.height;
    closeCamera();
    setImage(source, width, height);
    trackVisualizer('photo_added', { photo_source: 'camera' });
  }

  function loadProject(project) {
    Object.assign(state, {
      id: project.id || createId('project'),
      imageUrl: project.imageUrl,
      imageWidth: project.imageWidth,
      imageHeight: project.imageHeight,
      segments: project.segments || [],
      nightLevel: project.nightLevel ?? 0.72,
      ledSpacing: project.ledSpacing ?? 18,
      brightness: project.brightness ?? 0.9,
      selectedPresetId: project.selectedPresetId || 'warm-white',
      selectedEffectId: Number.isInteger(project.selectedEffectId) ? project.selectedEffectId : null,
      effectSpeed: Number.isFinite(Number(project.effectSpeed)) ? core.clamp(Math.round(Number(project.effectSpeed)), 0, 255) : core.DEFAULT_EFFECT_SPEED,
      effectIntensity: Number.isFinite(Number(project.effectIntensity)) ? core.clamp(Math.round(Number(project.effectIntensity)), 0, 255) : 128,
      effectCustom: Array.isArray(project.effectCustom) ? project.effectCustom.slice(0, 3).map((value, index) => core.clamp(Math.round(Number(value) || 0), 0, index === 2 ? 31 : 255)) : [128, 128, 16],
      effectOptions: Array.isArray(project.effectOptions) ? project.effectOptions.slice(0, 3).map(Boolean) : [false, false, false],
      selectedPaletteId: Number.isInteger(project.selectedPaletteId) ? core.clamp(project.selectedPaletteId, 0, 255) : 0,
      customColors: normalizedControlColors(project.customColors, project.customColors?.length || 3),
      selectedColorSlot: Number.isInteger(project.selectedColorSlot)
        ? core.clamp(project.selectedColorSlot, 0, Math.max(0, Math.min(5, project.customColors?.length || 3) - 1))
        : 0,
      colorsCustomized: project.colorsCustomized === true,
      speedCustomized: project.speedCustomized === true,
      intensityCustomized: project.intensityCustomized === true,
      paletteCustomized: project.paletteCustomized === true,
      appTab: ['colors', 'effects', 'generator', 'presets'].includes(project.appTab) ? project.appTab : 'colors',
      poweredOn: project.poweredOn !== false
    });
    image.src = state.imageUrl;
    stage.style.aspectRatio = `${state.imageWidth} / ${state.imageHeight}`;
    uploadView.hidden = true;
    workspace.hidden = false;
    nightSlider.value = String(Math.round(state.nightLevel * 100));
    brightnessSlider.value = String(Math.round(state.brightness * 100));
    appBrightness.value = String(Math.round(state.brightness * 255));
    spacingSlider.value = String(state.ledSpacing);
    $('[data-brightness-output]').textContent = `${brightnessSlider.value}%`;
    syncControlOutputs();
    updateSpacingOutput();
    updateNightFilter();
    syncPowerControl();
    setGuidesVisible(true);
    history = [JSON.stringify(state.segments)];
    historyIndex = 0;
    renderPresets();
    renderEffects();
    renderColorControls();
    renderPaletteControl();
    switchAppTab(state.appTab, false);
    updateNowPlaying();
    requestAnimationFrame(() => {
      fitStageToScreen();
      resizeLayers();
    });
  }

  function resetProject() {
    closeCamera();
    core.projectStorage.clear();
    state.imageUrl = '';
    state.segments = [];
    state.selectedSegmentId = null;
    state.selectedPointIndex = null;
    state.activeSegmentId = null;
    image.removeAttribute('src');
    workspace.hidden = true;
    uploadView.hidden = false;
    photoInput.value = '';
    uploadMessage.textContent = '';
    resumeButton.hidden = true;
    if (confirmDialog.open) confirmDialog.close();
    uploadView.scrollIntoView({ behavior: 'smooth', block: 'start' });
    updateSteps(1);
  }

  function updateSpacingOutput() {
    const value = state.ledSpacing;
    $('[data-spacing-output]').textContent = value <= 14 ? 'Close' : value >= 24 ? 'Wide' : 'Medium';
  }

  photoInput.addEventListener('change', () => processFile(photoInput.files?.[0]));
  $('[data-open-camera]').addEventListener('click', openCamera);
  $('[data-close-camera]').addEventListener('click', closeCamera);
  $('[data-capture-photo]').addEventListener('click', capturePhoto);
  cameraDialog.addEventListener('close', closeCamera);
  window.addEventListener('orientationchange', updateCameraOrientation);
  window.addEventListener('resize', updateCameraOrientation);
  window.visualViewport?.addEventListener('resize', updateCameraOrientation);

  traceLayer.addEventListener('pointerdown', addPoint);
  traceLayer.addEventListener('pointermove', moveDrag);
  traceLayer.addEventListener('pointerup', endDrag);
  traceLayer.addEventListener('pointercancel', endDrag);

  $('[data-tool="draw"]').addEventListener('click', () => setGuidesVisible(true));

  $('[data-new-segment]').addEventListener('click', () => {
    setGuidesVisible(true);
    state.activeSegmentId = null;
    state.selectedSegmentId = null;
    state.selectedPointIndex = null;
    renderTrace();
  });
  $('[data-finish-segment]').addEventListener('click', () => {
    const finishedSegment = activeSegment();
    state.activeSegmentId = null;
    state.selectedPointIndex = null;
    renderTrace();
    setGuidesVisible(false);
    updateSteps(3);
    if (finishedSegment?.points.length >= 2) {
      trackVisualizer('roofline_completed', { point_count: finishedSegment.points.length, run_count: state.segments.length });
    }
  });
  $('[data-toggle-guides]').addEventListener('click', () => setGuidesVisible(!state.showGuides));
  $('[data-delete-point]').addEventListener('click', () => {
    const segment = selectedSegment();
    if (!segment || state.selectedPointIndex === null) return;
    segment.points.splice(state.selectedPointIndex, 1);
    if (!segment.points.length) state.segments = state.segments.filter((entry) => entry.id !== segment.id);
    state.selectedPointIndex = null;
    pushHistory();
    renderTrace();
  });
  $('[data-delete-segment]').addEventListener('click', () => {
    if (!state.selectedSegmentId) return;
    state.segments = state.segments.filter((segment) => segment.id !== state.selectedSegmentId);
    state.selectedSegmentId = null;
    state.selectedPointIndex = null;
    state.activeSegmentId = null;
    pushHistory();
    renderTrace();
  });
  $('[data-clear-segments]').addEventListener('click', () => {
    state.segments = [];
    state.selectedSegmentId = null;
    state.selectedPointIndex = null;
    state.activeSegmentId = null;
    pushHistory();
    renderTrace();
  });
  $('[data-undo]').addEventListener('click', () => restoreHistory(historyIndex - 1));
  $('[data-redo]').addEventListener('click', () => restoreHistory(historyIndex + 1));

  nightSlider.addEventListener('input', () => {
    state.nightLevel = Number(nightSlider.value) / 100;
    updateNightFilter();
    scheduleSave();
    updateSteps(3);
  });
  brightnessSlider.addEventListener('input', () => {
    state.brightness = Number(brightnessSlider.value) / 100;
    appBrightness.value = String(Math.round(state.brightness * 255));
    $('[data-brightness-output]').textContent = `${brightnessSlider.value}%`;
    $('[data-app-brightness-output]').textContent = `${brightnessSlider.value}%`;
    scheduleSave();
  });
  appBrightness.addEventListener('input', () => {
    state.brightness = Number(appBrightness.value) / 255;
    brightnessSlider.value = String(Math.round(state.brightness * 100));
    $('[data-brightness-output]').textContent = `${brightnessSlider.value}%`;
    $('[data-app-brightness-output]').textContent = `${brightnessSlider.value}%`;
    scheduleSave();
  });
  spacingSlider.addEventListener('input', () => {
    state.ledSpacing = Number(spacingSlider.value);
    updateSpacingOutput();
    updateCounts();
    scheduleSave();
  });
  $('[data-zoom-slider]').addEventListener('input', (event) => {
    state.zoom = Number(event.target.value) / 100;
    stage.style.width = `${Math.round(fittedStageWidth * state.zoom)}px`;
    $('[data-zoom-output]').textContent = `${event.target.value}%`;
    requestAnimationFrame(resizeLayers);
  });
  $('[data-fit-view]').addEventListener('click', () => {
    state.zoom = 1;
    $('[data-zoom-slider]').value = '100';
    $('[data-zoom-output]').textContent = '100%';
    fitStageToScreen();
    stageWrap.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
    requestAnimationFrame(resizeLayers);
  });
  $('[data-play-toggle]').addEventListener('click', (event) => {
    state.poweredOn = !state.poweredOn;
    animationStarted = performance.now();
    syncPowerControl();
    scheduleSave();
  });
  presetSearch.addEventListener('input', () => {
    presetSearchQuery = presetSearch.value.slice(0, 80);
    renderPresets();
  });
  presetBack.addEventListener('click', () => {
    const currentFolder = presetCatalog?.folders.find((folder) => folder.id === currentPresetFolderId);
    if (!currentFolder?.parentId) return;
    currentPresetFolderId = currentFolder.parentId;
    renderPresets();
    presetBrowser.scrollTop = 0;
  });
  effectSearch.addEventListener('input', () => {
    effectSearchQuery = effectSearch.value.slice(0, 80);
    renderEffects();
  });
  root.querySelectorAll('[data-app-effect-filter]').forEach((button) => button.addEventListener('click', () => {
    effectFilter = button.dataset.appEffectFilter;
    root.querySelectorAll('[data-app-effect-filter]').forEach((other) => other.setAttribute('aria-pressed', String(other === button)));
    renderEffects();
  }));
  paletteSearch.addEventListener('input', () => {
    paletteSearchQuery = paletteSearch.value.slice(0, 80);
    renderPalettes();
  });
  $('[data-add-palette]').addEventListener('click', () => {
    paletteNote.textContent = 'Custom palettes can be created and synced from the full ShowHome app.';
  });
  root.querySelectorAll('[data-app-tab]').forEach((button) => {
    button.addEventListener('click', () => switchAppTab(button.dataset.appTab));
  });
  // Keep mouse-wheel and trackpad movement inside the simulated app. Each tab
  // has its own scroll container; forward the gesture to whichever one is
  // active so long palette lists remain reachable beneath the colour wheel.
  root.querySelector('.shl-phone').addEventListener('wheel', (event) => {
    if (event.target instanceof Element && event.target.closest('[data-generator-suggestions], [data-generator-browse-list]')) return;
    const activePanel = root.querySelector('[data-app-panel].is-active');
    const scrollPanel = activePanel?.querySelector('.shl-phone__catalog, .shl-color-control, .shl-generator');
    if (!scrollPanel || scrollPanel.scrollHeight <= scrollPanel.clientHeight) return;
    const canScroll = event.deltaY < 0
      ? scrollPanel.scrollTop > 0
      : scrollPanel.scrollTop + scrollPanel.clientHeight < scrollPanel.scrollHeight - 1;
    if (!canScroll) { event.preventDefault(); return; }
    scrollPanel.scrollTop += event.deltaY;
    event.preventDefault();
  }, { passive: false });
  colorWheel.addEventListener('pointerdown', (event) => {
    colorWheelDragging = true;
    colorWheel.setPointerCapture(event.pointerId);
    updateColorFromWheel(event);
  });
  colorWheel.addEventListener('pointermove', (event) => {
    if (colorWheelDragging) updateColorFromWheel(event);
  });
  const finishColorWheel = () => { colorWheelDragging = false; };
  colorWheel.addEventListener('pointerup', finishColorWheel);
  colorWheel.addEventListener('pointercancel', finishColorWheel);
  colorWheel.addEventListener('keydown', (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const hsv = rgbToHsv(state.customColors[state.selectedColorSlot]);
    if (event.key === 'ArrowLeft') hsv.h = (hsv.h + 357) % 360;
    if (event.key === 'ArrowRight') hsv.h = (hsv.h + 3) % 360;
    if (event.key === 'ArrowUp') hsv.s = core.clamp(hsv.s + 0.03);
    if (event.key === 'ArrowDown') hsv.s = core.clamp(hsv.s - 0.03);
    setSelectedColor(hsvToRgb(hsv.h, hsv.s, hsv.v));
  });
  colorValue.addEventListener('input', () => {
    const hsv = rgbToHsv(state.customColors[state.selectedColorSlot]);
    setSelectedColor(hsvToRgb(hsv.h, hsv.s, Number(colorValue.value) / 255));
  });
  colorTemperature.addEventListener('input', () => {
    setSelectedColor(colorFromTemperatureValue(colorTemperature.value));
  });
  root.querySelectorAll('[data-quick-color]').forEach((button) => {
    button.addEventListener('click', () => setSelectedColor(hexToRgb(button.dataset.quickColor)));
  });
  $('[data-random-color]').addEventListener('click', () => {
    setSelectedColor([0, 0, 0].map(() => Math.floor(Math.random() * 256)));
  });
  $('[data-apply-color-hex]').addEventListener('click', () => {
    const color = hexToRgb(colorHex.value);
    colorHex.toggleAttribute('aria-invalid', !color);
    if (color) setSelectedColor(color);
  });
  colorHex.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') $('[data-apply-color-hex]').click();
  });
  $('[data-save-project]').addEventListener('click', () => {
    try {
      core.projectStorage.save(projectSnapshot());
      $('[data-save-status]').textContent = 'Design saved in this browser.';
    } catch (_error) {
      $('[data-save-status]').textContent = 'This photo is too large to save locally, but it remains available in this tab.';
    }
  });
  $('[data-start-over]').addEventListener('click', () => confirmDialog.showModal());
  $('[data-cancel-reset]').addEventListener('click', () => confirmDialog.close());
  $('[data-confirm-reset]').addEventListener('click', resetProject);
  root.querySelectorAll('[data-open-help]').forEach((button) => button.addEventListener('click', () => helpDialog.showModal()));
  root.querySelectorAll('[data-close-help]').forEach((button) => button.addEventListener('click', () => helpDialog.close()));
  helpDialog.addEventListener('click', (event) => {
    if (event.target === helpDialog) helpDialog.close();
  });
  const fullAppFeatureCopy = {
    'App menu': 'Device setup, controller management, account settings, and advanced app options are available after connecting your lights in the full mobile app.',
    Announcements: 'Product news, app updates, and connected-device notices are available in the full mobile app.',
    Zones: 'Create zones and control different areas of your home independently after connecting your ShowHome controller in the full mobile app.',
    Schedules: 'Automatically turn lights on, change presets, or turn them off at selected times using schedules in the full mobile app.',
    Peek: 'Peek works with connected ShowHome hardware so you can inspect the current output of your installed lighting system.',
    Individual: 'Select and fine-tune individual lights on an installed system using the advanced controls in the full mobile app.'
  };
  root.querySelectorAll('[data-full-app-feature]').forEach((button) => button.addEventListener('click', () => {
    const feature = button.dataset.fullAppFeature || 'This feature';
    fullAppHeading.textContent = `${feature}: available in the full app`;
    fullAppCopy.textContent = fullAppFeatureCopy[feature] || 'Connect your ShowHome controller to use this feature in the full mobile app.';
    fullAppDialog.showModal();
  }));
  root.querySelectorAll('[data-close-full-app]').forEach((button) => button.addEventListener('click', () => fullAppDialog.close()));
  fullAppDialog.addEventListener('click', (event) => {
    if (event.target === fullAppDialog) fullAppDialog.close();
  });
  $('[data-quote-link]').addEventListener('click', () => trackVisualizer('quote_clicked', { run_count: state.segments.length }));
  root.querySelectorAll('[data-feedback-link]').forEach((link) => link.addEventListener('click', () => trackVisualizer('feedback_clicked')));

  root.querySelectorAll('[data-step-button]').forEach((button) => button.addEventListener('click', () => {
    const step = Number(button.dataset.stepButton);
    if (!state.imageUrl) return uploadView.scrollIntoView({ behavior: 'smooth' });
    updateSteps(step);
    if (step <= 2) stageWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
    else root.querySelector(step === 3 ? '.shl-config__panel' : '.shl-config__panel--presets').scrollIntoView({ behavior: 'smooth', block: 'center' });
  }));

  document.addEventListener('keydown', (event) => {
    if (workspace.hidden || !root.isConnected) return;
    if (event.target instanceof Element && event.target.closest('.shl-phone')) return;
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      restoreHistory(historyIndex + (event.shiftKey ? 1 : -1));
    } else if ((event.key === 'Delete' || event.key === 'Backspace') && !['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) {
      const segment = selectedSegment();
      if (segment && state.selectedPointIndex !== null) $('[data-delete-point]').click();
    }
  });

  const handleViewportResize = () => requestAnimationFrame(() => {
    fitStageToScreen();
    resizeLayers();
  });
  const observer = new ResizeObserver(handleViewportResize);
  observer.observe(stageWrap);
  window.addEventListener('resize', handleViewportResize);
  window.addEventListener('pagehide', () => {
    cancelAnimationFrame(animationFrame);
    observer.disconnect();
    window.removeEventListener('resize', handleViewportResize);
    window.removeEventListener('orientationchange', updateCameraOrientation);
    window.removeEventListener('resize', updateCameraOrientation);
    window.visualViewport?.removeEventListener('resize', updateCameraOrientation);
    designer.destroy();
    closeCamera();
  }, { once: true });

  const designer = generatorModule.createPresetDesigner(root, {
    presetModule,
    onPreview(look) {
      const effect = effects.find((entry) => entry.id === look.effectId);
      if (!effect) return;
      // Repeat only the chosen palette into spare firmware slots; never let
      // unrelated previous colours leak into a generated one/two-colour look.
      const colors = Array.from({ length: 5 }, (_, index) => look.colors[index % look.colors.length]);
      selectEffect(effect, colors, look);
    }
  });
  presets = core.DEMO_PRESETS.map((preset) => ({ ...preset }));
  animationFrame = requestAnimationFrame(drawFrame);
  const visibleEffectCount = () => effects.filter((effect) => effect.visibleInApp !== false).length;
  const updateCatalogStatus = () => {
    if (presetCatalogLoaded && effectCatalogLoaded) {
      presetStatus.textContent = `${presets.length} presets + ${visibleEffectCount()} effects • Secure Shopify catalogue — no database connection`;
    } else if (presetCatalogLoaded) {
      presetStatus.textContent = `${presets.length} global presets • Secure snapshot — no database connection`;
    }
  };
  presetRepository.getCatalog().then((catalog) => {
    presetCatalog = catalog;
    presets = catalog.presets;
    hydratePresetMetadata();
    presetCatalogLoaded = catalog.source === 'snapshot';
    currentPresetFolderId = catalog.rootFolderId;
    if (!presets.some((preset) => preset.id === state.selectedPresetId)) {
      state.selectedPresetId = presets[0]?.id || state.selectedPresetId;
      animationStarted = performance.now();
    }
    presetSearch.placeholder = `Search ${presets.length} app presets`;
    if (!presetCatalogLoaded) presetStatus.textContent = 'Preset snapshot is temporarily unavailable • Showing built-in previews';
    else updateCatalogStatus();
    renderPresets();
    if (state.selectedEffectId === null && !state.colorsCustomized && !state.speedCustomized && !state.intensityCustomized) {
      const preset = selectedPreset();
      const firstSegment = preset?.segments?.[0] || {};
      state.customColors = normalizedControlColors(firstSegment.colors || preset?.colors, 5);
      state.effectSpeed = Number.isFinite(Number(firstSegment.speed)) ? Number(firstSegment.speed) : 128;
      state.effectIntensity = Number.isFinite(Number(firstSegment.intensity)) ? Number(firstSegment.intensity) : 128;
      state.selectedPaletteId = Number.isFinite(Number(firstSegment.palette)) ? Number(firstSegment.palette) : 0;
      state.paletteCustomized = false;
      syncControlOutputs();
      renderColorControls();
    }
    renderPalettes();
    renderPaletteControl();
    updateNowPlaying();
  });
  effectRepository.getEffects().then((catalogEffects) => {
    effects = catalogEffects;
    designer.setEffects(effects);
    hydratePresetMetadata();
    effectCatalogLoaded = true;
    if (state.selectedEffectId !== null && !effects.some((effect) => effect.id === state.selectedEffectId)) state.selectedEffectId = null;
    state.customColors = normalizedControlColors(state.customColors, activeColorSlotCount());
    state.selectedColorSlot = core.clamp(state.selectedColorSlot, 0, state.customColors.length - 1);
    effectSearch.placeholder = `Search ${visibleEffectCount()} effects`;
    renderEffects();
    renderColorControls();
    renderPaletteControl();
    updateNowPlaying();
    updateCatalogStatus();
  }).catch(() => {
    effectBrowser.replaceChildren();
    const message = document.createElement('p');
    message.className = 'shl-phone__empty';
    message.textContent = 'Effects are temporarily unavailable. Presets and colors still work.';
    effectBrowser.append(message);
    designer.setEffects([]);
  });
  paletteRepository.getPalettes().then((catalogPalettes) => {
    palettes = catalogPalettes;
    hydratePresetMetadata();
    if (!palettes.some((palette) => palette.id === state.selectedPaletteId)) state.selectedPaletteId = 0;
    syncControlOutputs();
    renderPalettes();
    renderPaletteControl();
  }).catch(() => {
    paletteControl.classList.add('is-disabled');
    paletteNote.textContent = '🎨 Palette catalogue is temporarily unavailable';
  });

  updateCameraOrientation();
  updateNightFilter();
  updateSpacingOutput();
  syncControlOutputs();
  renderColorControls();
  renderPalettes();
  renderPaletteControl();
  switchAppTab(state.appTab, false);
  setGuidesVisible(true);
  const savedProject = core.projectStorage.load();
  if (isEmbeddedStockDemo) {
    loadProject({
      id: 'stock-house-demo',
      imageUrl: root.dataset.stockHouseUrl,
      imageWidth: 1672,
      imageHeight: 941,
      segments: [
        { id: 'stock-run-1', points: [{ x: 0.116, y: 0.276 }, { x: 0.806, y: 0.289 }] },
        { id: 'stock-run-2', points: [{ x: 0.317, y: 0.484 }, { x: 0.65, y: 0.485 }] },
        { id: 'stock-run-3', points: [{ x: 0.604, y: 0.549 }, { x: 0.909, y: 0.55 }] }
      ],
      nightLevel: 0.78,
      ledSpacing: 18,
      brightness: 0.9,
      selectedPresetId: 'warm-white',
      selectedEffectId: 0,
      effectSpeed: core.DEFAULT_EFFECT_SPEED,
      effectIntensity: 128,
      effectCustom: [128, 128, 16],
      effectOptions: [false, false, false],
      selectedPaletteId: 0,
      customColors: [[255, 255, 255], [255, 220, 160], [255, 255, 255], [255, 200, 0], [255, 0, 220]],
      selectedColorSlot: 0,
      appTab: 'generator',
      poweredOn: true
    });
    setGuidesVisible(false);
  } else if (savedProject?.imageUrl) {
    resumeButton.hidden = false;
    resumeButton.addEventListener('click', () => loadProject(savedProject), { once: true });
  }
}
